import { FC } from 'react'
import { useQuery } from 'react-query'
import {
  downloadArquivo,
  IconPaths,
  ModalSize,
  showError,
} from '../../../../../../_metronic/helpers'
import { Button, useModal } from '../../../../../components'
import { AtendimentoView } from '../../../atendimento/core/_models'
import { ExamesModal } from '../../../atendimento-ASO/components/modal'
import { AnexoInterconsulta } from '../../../pareceres/pareceres-list/core/_models'
import {
  baixarAnexoInterconsulta,
  getAnexosInterconsulta,
} from '../../../pareceres/pareceres-list/core/_requests'
import {
  chaveAnexosInterconsulta,
  isExame,
  nomeDoAnexo,
} from '../../../pareceres/pareceres-list/components/modal/AnexosInterconsultaModal'

type Props = {
  atendimento: AtendimentoView
}

/**
 * Exames da solicitação, sempre à vista ao lado do parecer: os anexos enviados pelo solicitante
 * (rotas próprias da interconsulta, mesma chave de cache do modal de anexos) e, em interconsultas
 * antigas, os procedimentos com anexo, que continuam no modal de exames.
 */
export const ExamesSolicitacao: FC<Props> = ({ atendimento }) => {
  const { showModal } = useModal()
  const atendimentoId = Number(atendimento.id)

  const { data: todos = [], isLoading } = useQuery(
    chaveAnexosInterconsulta(atendimentoId),
    () => getAnexosInterconsulta(atendimentoId),
    { retry: false, onError: showError }
  )
  const anexos = todos.filter(isExame)
  const qtdProcedimentos = atendimento.procedimentos?.length ?? 0

  const baixar = async (anexo: AnexoInterconsulta) => {
    try {
      await downloadArquivo(await baixarAnexoInterconsulta(atendimentoId, anexo.id))
    } catch (error) {
      showError(error)
    }
  }

  const verProcedimentos = () =>
    showModal({
      title: `Exames - ${atendimento.protocolo ?? ''}`,
      content: <ExamesModal isAtendimento atendimentoId={atendimentoId} />,
      size: ModalSize.Large,
    })

  return (
    <section aria-labelledby='parecer-exames-titulo' className='card card-bordered'>
      <div className='card-body p-5'>
        <h2 id='parecer-exames-titulo' className='fs-6 fw-bold mb-3'>
          Exames anexados {!isLoading && `(${anexos.length + qtdProcedimentos})`}
        </h2>
        {isLoading ? (
          <p className='text-muted mb-0'>Carregando exames...</p>
        ) : anexos.length === 0 && qtdProcedimentos === 0 ? (
          <p className='text-muted mb-0'>
            O solicitante não enviou exames. Se precisar de algum, registre no parecer.
          </p>
        ) : (
          <ul className='list-unstyled d-flex flex-column gap-2 mb-0'>
            {anexos.map((anexo) => (
              <li
                key={anexo.id}
                className='d-flex align-items-center gap-3 border rounded px-3 py-2'
              >
                <div className='flex-grow-1 text-truncate'>
                  <div className='fw-semibold text-gray-900 text-truncate'>
                    {nomeDoAnexo(anexo)}
                  </div>
                  {anexo.dataFormatada && (
                    <div className='text-muted fs-7'>{anexo.dataFormatada}</div>
                  )}
                </div>
                <Button
                  variant='secondary-outline'
                  size='btn-sm'
                  icon={IconPaths.Download}
                  onClick={() => baixar(anexo)}
                  ariaLabel={`Baixar ${nomeDoAnexo(anexo)}`}
                >
                  Baixar
                </Button>
              </li>
            ))}
            {qtdProcedimentos > 0 && (
              <li>
                <Button
                  variant='secondary-outline'
                  size='btn-sm'
                  icon={IconPaths.Pdf}
                  onClick={verProcedimentos}
                >
                  Ver exames de procedimento ({qtdProcedimentos})
                </Button>
              </li>
            )}
          </ul>
        )}
      </div>
    </section>
  )
}
